import { atom, useRecoilCallback, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { DEFAULT_AMOUNT_OF_ROOMS } from '/components/BreakoutRooms/helpers/constants';

const generateDefaultRooms = () =>
  [...Array(DEFAULT_AMOUNT_OF_ROOMS)].map(() => uuidv4());

const roomsState = atom<Array<string>>({
  key: 'rooms-state',
  default: generateDefaultRooms(),
});

export const useRooms = () => {
  const rooms = useRecoilValue(roomsState);
  const addRoom = useRecoilCallback(
    ({ set }) =>
      (newRoomId: string) => {
        set(roomsState, (state) => [...state, newRoomId]);
      },
    []
  );

  const deleteRoom = useRecoilCallback(
    ({ set }) =>
      (roomIdentifier: string) => {
        set(roomsState, (state) =>
          state.filter((roomId) => roomId !== roomIdentifier)
        );
      },
    []
  );

  /* We need to generate a new unique ID for room 1 everytime a new breakout session is started.
   * Normally, we'd use "reset" to reset the room state. Doing so however will return the same UUID
   * for room 1 in the new session -- that UUID generated when this hook is first called is its default and
   * won't change. This causes issues with the chat: anyone will be able to read the chat messages
   * in room 1, since its roomId persists over the entirety of the call.
   */
  const deleteAllRooms = useRecoilCallback(
    ({ set }) =>
      () => {
        set(roomsState, generateDefaultRooms);
      },
    []
  );

  return {
    addRoom,
    deleteAllRooms,
    deleteRoom,
    rooms,
  };
};
